import React from "react";
import { LegalLayout, Section, Bullets } from "@/components/legal/LegalLayout";
import { BILLING_EMAIL } from "@/constants/legal";

export default function SubscriptionPolicy() {
  return (
    <LegalLayout
      slug="subscription-policy"
      title="Subscription & Billing Policy"
      subtitle="This policy explains how HostGuard subscriptions are billed, changed and cancelled. All prices are in Australian Dollars (AUD) and include GST where applicable."
    >
      <Section title="1. Plans and pricing">
        <p>HostGuard offers a Free plan and paid plans (Pro Host at $19/month and Property Manager at $99/month). The Free plan includes a limited number of assessments per month; paid plans unlock unlimited assessments, AI summaries and additional features as described on our <a href="/pricing" className="font-medium text-primary hover:underline">Pricing page</a>.</p>
      </Section>

      <Section title="2. Monthly recurring billing">
        <p>Paid plans are billed in advance on a recurring monthly cycle. Your billing date is the date you first subscribe, and your subscription renews automatically each month on that date until cancelled. By subscribing you authorise these recurring charges.</p>
      </Section>

      <Section title="3. Payment processing (Stripe)">
        <p>All payments are processed securely by Stripe. HostGuard does not store your full card details. You agree to Stripe handling your payment information in accordance with their terms and security standards.</p>
      </Section>

      <Section title="4. Plan upgrades">
        <Bullets items={[
          "You can upgrade at any time (for example, Free → Pro Host, or Pro Host → Property Manager).",
          "Upgrades take effect immediately upon successful payment, giving you instant access to the higher plan’s features.",
        ]} />
      </Section>

      <Section title="5. Plan downgrades">
        <Bullets items={[
          "You can downgrade at any time, including to the Free plan.",
          "Downgrades take effect at the end of your current paid billing period, so you keep paid access until that period ends.",
          "After the period ends, your account reverts to the lower (or Free) plan and its limits apply.",
        ]} />
      </Section>

      <Section title="6. Cancellation">
        <Bullets items={[
          "You can cancel your subscription at any time from Settings → Subscription & billing.",
          "Cancellation stops future renewals. You retain paid access until the end of the current billing period, after which your account reverts to the Free plan.",
          "If you change your mind before the period ends, you can resume your subscription to keep it active.",
        ]} />
      </Section>

      <Section title="7. Failed payments">
        <p>If a renewal payment fails, we may retry the charge. If payment cannot be collected, paid features may be paused or your account may revert to the Free plan. You can update your payment method to restore access. We are not responsible for fees your bank may charge for failed or retried transactions.</p>
      </Section>

      <Section title="8. Subscription termination">
        <p>We may terminate or suspend a subscription for breach of our Terms or Acceptable Use Policy, for non-payment, or where required by law. Where we terminate without cause, we will provide a pro-rata refund of any prepaid, unused period where required by law.</p>
      </Section>

      <Section title="9. Billing disputes">
        <p>If you believe you have been charged in error, contact us at <a href={`mailto:${BILLING_EMAIL}`} className="font-medium text-primary hover:underline">{BILLING_EMAIL}</a> within 30 days of the charge. We will investigate and respond promptly. Please also review our <a href="/refund-policy" className="font-medium text-primary hover:underline">Refund Policy</a>.</p>
      </Section>
    </LegalLayout>
  );
}
